/**
 * Sanctum stateful (cookie) auth requires the CSRF dance:
 *
 *   1. GET /sanctum/csrf-cookie — the server sets an `XSRF-TOKEN` cookie.
 *   2. Every mutating request echoes that cookie back in an `X-XSRF-TOKEN`
 *      header, which is what proves the request came from our own page and not
 *      from a form on someone else's.
 *
 * The cookie is deliberately readable by JavaScript — that is how the scheme
 * works. The *session* cookie is HttpOnly and never touched here.
 */

import { apiErrorFromResponse } from './errors'

const CSRF_COOKIE = 'XSRF-TOKEN'
const CSRF_HEADER = 'X-XSRF-TOKEN'

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS', 'TRACE'])

export function readCsrfToken(): string | null {
  if (typeof document === 'undefined') {
    return null
  }

  const match = document.cookie.split('; ').find((entry) => entry.startsWith(`${CSRF_COOKIE}=`))

  if (!match) {
    return null
  }

  // Laravel URL-encodes the value; the header must carry it decoded.
  return decodeURIComponent(match.slice(CSRF_COOKIE.length + 1))
}

export function needsCsrf(method: string): boolean {
  return !SAFE_METHODS.has(method.toUpperCase())
}

/**
 * Fetch a fresh CSRF cookie.
 *
 * In-flight requests are shared: a page that fires several mutations at once
 * after the token expires should hit this endpoint once, not once per request.
 */
let inFlight: Promise<void> | null = null

export function ensureCsrfCookie(baseUrl: string): Promise<void> {
  if (inFlight) {
    return inFlight
  }

  inFlight = fetch(`${baseUrl.replace(/\/$/, '')}/sanctum/csrf-cookie`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
    .then(async (response) => {
      if (!response.ok) {
        throw await apiErrorFromResponse(response)
      }
    })
    .finally(() => {
      inFlight = null
    })

  return inFlight
}

export { CSRF_HEADER }
